import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { STUDIO_FORMATS, StudioFormat, StudioTheme, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type { LyricSyncState } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../common/types';
import {
  shortSection,
  showsReadyChip,
  stateHasNote,
  StudioSongContext,
  themeState,
  visibleThemes,
} from './studioRules';
import { ThemeCard } from './ThemeCard';
import { ThemeCardNote } from './ThemeCardNote';
import { LyricSyncNotice } from './LyricSyncNotice';
import { AssetPreviewTheme, AssetTypePreview } from './AssetTypePreview';
import { studioMessages } from './studioMessages';
import { studioFormatMessages } from './studioThemeMessages';
import { hasCardWork, StudioCardWork } from './studioCardWork';
import { ThemeCardWorkActions } from './ThemeCardWork';
import type { SongLyricsView } from '../../../smartLinks/catalog/song/page/catalogSongPath';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from './Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

const NO_OP = (): void => undefined;
const NO_WORK: ReadonlyMap<StudioThemeId, StudioCardWork> = new Map();

interface AssetTypeScreenProps {
  song: StudioSongContext & { lyricSyncState?: LyricSyncState };
  /** The theme of the song: each card draws its picture in it (design q3a). */
  previewTheme: AssetPreviewTheme;
  /** The owner of the workspace sees "Upgrade" on the Free cards; a member sees whom to ask (s7c). */
  isOwner?: boolean;
  /** Opens the Lyrics section of the song page in Catalog ("Add the lyrics", "Fix the times"). */
  onOpenSongLyrics?: (view?: SongLyricsView) => void;
  /** An old link `?format=` scrolls to that group. */
  focusFormat?: StudioFormat;
  onPick: (themeId: StudioThemeId) => void;
  /** The work of each asset type for the song (`useStudioCardWork`). */
  work?: ReadonlyMap<StudioThemeId, StudioCardWork>;
  /**
   * "See the work" under a card: the work of the song in the home of its kind (design
   * assets-rethink a1: a keeper in the Kit tab, a post on Posts).
   */
  onOpenWork?: (state: 'working' | 'failed', themeId: StudioThemeId) => void;
  /** "Open" under a card: the result of its newest ready run. */
  onOpenReady?: (work: StudioCardWork) => void;
  /** More cards at the end of the Full videos group (the directed video beta, design directed-video a4). */
  fullExtra?: React.ReactNode;
}

interface ThemeGroup {
  key: string;
  title?: string;
  themes: StudioTheme[];
}

const groupId = (format: StudioFormat): string => `studio-assets-${format}`;

/**
 * The asset types of the Studio on one screen (design release-kit t2; it replaces the format step
 * l1 and step 2 of l2–l4): Images, Short videos and Full videos, each card with a picture in the
 * song's theme (q3a), its cost and its state (s7c). The chips at the top jump to a group; they do
 * not filter. The AI asset types show only with `canUseAiReels` (D17). A card that is shut says
 * why and what to do. A click opens the details step of the card. A card with work of the song
 * shows it ("1 making", "2 ready", "1 failed") and, under it, "Open" and "See the work".
 */
const AssetTypeScreenComponent: React.FC<AssetTypeScreenProps> = (props) => {
  const {
    song,
    previewTheme,
    isOwner = false,
    onOpenSongLyrics = NO_OP,
    focusFormat,
    onPick,
    work = NO_WORK,
    onOpenWork = NO_OP,
    onOpenReady = NO_OP,
    fullExtra,
  } = props;
  const intl = useIntl();
  const { canUseAiReels } = song;

  const formats = useMemo(
    () =>
      STUDIO_FORMATS.map((format) => {
        const themes = visibleThemes(format, canUseAiReels);
        const groups: ThemeGroup[] =
          format === 'short'
            ? [
                {
                  key: 'social',
                  title: intl.formatMessage(studioMessages.sectionSocial),
                  themes: themes.filter((theme) => shortSection(theme) === 'social'),
                },
                {
                  key: 'loops',
                  title: intl.formatMessage(studioMessages.sectionLoops),
                  themes: themes.filter((theme) => shortSection(theme) === 'loops'),
                },
              ]
            : [{ key: format, themes }];
        return { format, count: themes.length, groups };
      }),
    [canUseAiReels, intl]
  );

  useEffect(() => {
    if (focusFormat !== undefined) {
      const target = document.getElementById(groupId(focusFormat));
      if (target !== null) {
        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 16 });
      }
    }
  }, [focusFormat]);

  return (
    <div className="d-flex flex-column gap-4">
      <nav className={styles.jumpChips} aria-label={intl.formatMessage(studioMessages.stepAsset)}>
        {formats.map(({ format, count }) => (
          <a key={format} className={styles.jumpChip} href={`#${groupId(format)}`}>
            {intl.formatMessage(studioFormatMessages[format].name)}
            <small>{count}</small>
          </a>
        ))}
      </nav>
      {song.hasLyrics && song.lyricSyncState !== undefined && (
        <LyricSyncNotice state={song.lyricSyncState} onOpenSongLyrics={onOpenSongLyrics} />
      )}
      {formats.map(({ format, groups }) => (
        <section key={format} id={groupId(format)} className={styles.assetGroup}>
          <div className={styles.assetGroupHead}>
            <h2 className={styles.assetGroupTitle}>{intl.formatMessage(studioFormatMessages[format].plural)}</h2>
            <span className={styles.mono}>{intl.formatMessage(studioFormatMessages[format].meta)}</span>
          </div>
          {groups.map((group) => (
            <div key={group.key}>
              {group.title !== undefined && <h3 className={styles.sectionTitle}>{group.title}</h3>}
              <div className={styles.grid}>
                {group.themes.map((theme) => {
                  const state = themeState(theme, song);
                  const cardWork = work.get(theme.id);
                  const stateNote = stateHasNote(state) ? (
                    <ThemeCardNote
                      themeId={theme.id}
                      state={state}
                      isOwner={isOwner}
                      onOpenSongLyrics={onOpenSongLyrics}
                    />
                  ) : undefined;
                  const workNote = hasCardWork(cardWork) ? (
                    <ThemeCardWorkActions
                      work={cardWork}
                      onOpenWork={(workState) => onOpenWork(workState, theme.id)}
                      onOpenReady={onOpenReady}
                    />
                  ) : undefined;
                  return (
                    <ThemeCard
                      key={theme.id}
                      theme={theme}
                      state={state}
                      showReady={showsReadyChip(theme.id)}
                      picture={<AssetTypePreview assetTypeId={theme.id} theme={previewTheme} />}
                      work={cardWork}
                      note={
                        stateNote === undefined && workNote === undefined ? undefined : (
                          <>
                            {stateNote}
                            {workNote}
                          </>
                        )
                      }
                      onPick={onPick}
                    />
                  );
                })}
                {format === 'full' && fullExtra}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
};

export const AssetTypeScreen = TypedMemo(AssetTypeScreenComponent);
